import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTran } from "@/hooks";
import { useAuthStore } from "@/features/auth/hooks";
import { reportingYearStore } from "@/features/reporting-boundary/stores/reporting-year.store";
import YearFilter from "../filters/YearFilter";
import FrameworkFilter from "../filters/FrameworkFilter";
import CategoryFilter from "../filters/CategoryFilter";
import SourceFilter from "../filters/SourceFilter";
import OrgFilter from "../filters/OrgFilter";
import SearchInput from "../filters/SearchInput";
import {
  useDataEntryFiltersStore,
  useEntryDataStore,
} from "../../hooks/useDataEntryStore";
import {
  collectSelectableOrgIds,
  makeTreewithAllowOrgIds,
} from "../../utils/orgTree.helpers";
import { normalizeOrgId } from "@/features/esg/utils/org-scope.utils";

const getOrganizationId = (organization) =>
  organization && typeof organization === "object"
    ? organization._id || organization.id || organization.organizationId
    : organization;

const OverviewFilterBar = observer(() => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const shared = useDataEntryFiltersStore();
  const { filteredOrgTree } = useEntryDataStore();
  const { reportingYears } = reportingYearStore;
  const f = shared.filters;

  const selectedReportingYears = useMemo(() => {
    if (!f.year || String(f.year).toLowerCase() === "all") return [];
    return reportingYears.filter(
      (reportingYear) => String(reportingYear.startYear) === String(f.year),
    );
  }, [f.year, reportingYears]);

  const reportingYearOrgIds = useMemo(() => {
    if (
      !selectedReportingYears.length ||
      selectedReportingYears.some(
        (reportingYear) => reportingYear.isAllOrganization,
      )
    ) {
      return null;
    }

    return new Set(
      selectedReportingYears
        .flatMap((reportingYear) => [
          ...(reportingYear.isCompanySelected ? [user?.companyId] : []),
          ...(reportingYear.organizations || []).map(getOrganizationId),
        ])
        .map(normalizeOrgId)
        .filter(Boolean),
    );
  }, [selectedReportingYears, user?.companyId]);

  const availableOrgTree = useMemo(() => {
    if (!reportingYearOrgIds) return filteredOrgTree;
    return makeTreewithAllowOrgIds(filteredOrgTree, reportingYearOrgIds);
  }, [filteredOrgTree, reportingYearOrgIds]);

  const selectableOrgIds = useMemo(
    () => collectSelectableOrgIds(availableOrgTree),
    [availableOrgTree],
  );

  useEffect(() => {
    if (!f.orgIds?.length) return;

    const selectableSet = new Set(selectableOrgIds.map(normalizeOrgId));
    const nextOrgIds = f.orgIds.filter((orgId) =>
      selectableSet.has(normalizeOrgId(orgId)),
    );

    if (nextOrgIds.length !== f.orgIds.length) {
      shared.setFilters({ orgIds: nextOrgIds });
    }
  }, [f.orgIds, selectableOrgIds, shared]);

  return (
    <div className="flex flex-wrap items-end gap-3 bg-muted/40 rounded-lg p-3">
      <YearFilter
        value={f.year}
        onChange={(v) => shared.setFilters({ year: v })}
        placeholder={t("All Years")}
      />

      <OrgFilter
        value={f.orgIds}
        onChange={(ids) => shared.setFilters({ orgIds: ids })}
        multiSelect
        showBranchSelectAll
        branchSelectLevels={["parent_company", "level_1"]}
        placeholder={t("All Organizations")}
        tree={availableOrgTree}
        showAllOption={selectableOrgIds.length > 1}
      />

      <FrameworkFilter
        value={f.framework}
        year={f.year}
        onChange={(v) => shared.setFilters({ framework: v })}
      />

      <CategoryFilter
        value={f.category}
        onChange={(v) => shared.setFilters({ category: v })}
      />

      <SourceFilter
        value={f.metricType}
        onChange={(v) => shared.setFilters({ metricType: v })}
      />

      <SearchInput
        value={f.search}
        onChange={(v) => shared.setFilters({ search: v })}
        placeholder={t("Search metric...")}
      />

      {shared.activeCount > 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => shared.reset()}
          className="gap-1.5"
        >
          <X size={14} />
          {t("Clear")}
          <Badge
            variant="secondary"
            className="ml-1 px-1.5 py-0 text-[10px] font-semibold"
          >
            {shared.activeCount}
          </Badge>
        </Button>
      )}
    </div>
  );
});

export default OverviewFilterBar;
